import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3 } from 'lucide-react';
import { DataQualitySnapshot } from '@/types/webSearch';
import DataQualityModal from './DataQualityModal';
import { computeOverallDataQuality, KeyContactsStats } from '@/utils/dataQualityUtils';
import { KeyContact } from '@/types/lead';

interface DataQualityCardProps {
  dataQuality?: DataQualitySnapshot | null;
  dataQualityHistory?: DataQualitySnapshot[];
  keyContacts?: KeyContact[];
}

function getScoreColor(score: number): string {
  if (score >= 76) return 'text-green-600 dark:text-green-400';
  if (score >= 51) return 'text-blue-600 dark:text-blue-400';
  if (score >= 26) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

function getProgressColor(score: number): string {
  if (score >= 76) return 'bg-green-500';
  if (score >= 51) return 'bg-blue-500';
  if (score >= 26) return 'bg-orange-500';
  return 'bg-red-500';
}

function getBadgeVariant(label: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (label === 'Excellent') return 'default';
  if (label === 'Good') return 'secondary';
  return 'outline';
}

const DataQualityCard: React.FC<DataQualityCardProps> = ({ dataQuality, dataQualityHistory, keyContacts }) => {
  if (!dataQuality) return null;

  const keyContactsStats: KeyContactsStats | undefined = keyContacts ? {
    total: keyContacts.length,
    withEmail: keyContacts.filter(c => !!c.email).length,
    withPhone: keyContacts.filter(c => !!c.phone).length,
  } : undefined;

  const overall = computeOverallDataQuality(dataQualityHistory || [], dataQuality, keyContactsStats);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Data Quality
          </CardTitle>
          <DataQualityModal dataQuality={dataQuality} dataQualityHistory={dataQualityHistory} keyContacts={keyContacts} />
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-bold ${getScoreColor(overall.score)}`}>
              {overall.score}
            </span>
            <span className="text-sm text-muted-foreground">/ 100</span>
          </div>
          <Badge variant={getBadgeVariant(overall.label)}>{overall.label}</Badge>
        </div>
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className={`h-full rounded-full transition-all ${getProgressColor(overall.score)}`}
            style={{ width: `${overall.score}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground">{overall.summary}</p>
      </CardContent>
    </Card>
  );
};

export default DataQualityCard;
